import React from 'react';
import { Alert, Textarea, TextInput } from '@parsina/framework';

import { useTranslation, LTR_TEXT } from '../../i18n';
import TaskDialog from '../../components/TaskDialog';
import { REASON_MAX, REASON_MIN, accountName } from './shared';

/**
 * Consolidate the previewed pair (PM-0536 P5b, findings 02 §3.3).
 *
 * Two things are MANDATORY, as on the server: the reason — the ID check the
 * decision rests on, written to the audit line — and the dropped account's
 * confirmation text (its username, which for a phone-first account is its sign-in
 * number) typed back. The confirm stays disabled until both hold, so the request
 * the BFF would refuse is never sent. Built on `TaskDialog`: it stays open with
 * every keystroke on a failed write, cannot double-submit, and asks before
 * discarding a typed reason (DESIGN_CONCEPT §2.4, §5.2).
 */
export default function ConsolidateDialog({
  preview, pending, error, onOpenChange, onSubmit,
}) {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState('');
  const [confirmation, setConfirmation] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  const keepId = preview?.keep?.id;
  const dropId = preview?.drop?.id;
  React.useEffect(() => { setReason(''); setConfirmation(''); setTouched(false); }, [keepId, dropId]);

  const trimmed = reason.trim();
  const reasonInvalid = trimmed.length < REASON_MIN;
  const expected = preview?.confirmationText || '';
  const confirmed = !!expected && confirmation.trim() === expected;

  return (
    <TaskDialog
      open={!!preview}
      onOpenChange={onOpenChange}
      title={t('accounts.consolidate.title')}
      error={error}
      pending={pending}
      dirty={!!(reason || confirmation)}
      submitLabel={t('accounts.consolidate.submit')}
      submitVariant="destructive"
      submitDisabled={reasonInvalid || !confirmed}
      onSubmit={() => {
        setTouched(true);
        if (!reasonInvalid && confirmed) onSubmit({ reason: trimmed, confirmation: confirmation.trim() });
      }}
      data-testid="accounts-consolidate"
      lead={(
        <Alert variant="error" data-testid="accounts-consolidate-warning">
          {t('accounts.consolidate.warning', {
            keep: accountName(preview?.keep), drop: accountName(preview?.drop),
          })}
        </Alert>
      )}
    >
      {preview && !preview.formerPhoneNumberKept ? (
        <Alert variant="warning" data-testid="accounts-consolidate-former-not-kept">
          {t('accounts.preview.formerNotKept')}
        </Alert>
      ) : null}
      <div data-testid="accounts-consolidate-reason">
        <Textarea
          label={t('accounts.consolidate.reason')}
          required
          helperText={t('accounts.consolidate.reasonHelp')}
          value={reason}
          onChange={(e) => setReason(e.target?.value ?? e)}
          onBlur={() => setTouched(true)}
          placeholder={t('accounts.consolidate.reasonPlaceholder')}
          error={touched && reason && reasonInvalid ? t('validation.tooShort', { min: REASON_MIN }) : undefined}
          maxLength={REASON_MAX}
          showCount
          rows={3}
          disabled={pending}
        />
      </div>
      <div className="mt-3">
        <p className="mb-1.5 text-sm text-muted-foreground" id="accounts-consolidate-expected">
          {t('accounts.consolidate.confirmHint')}
          {' '}
          <strong {...LTR_TEXT} className="font-mono text-foreground" data-testid="accounts-consolidate-expected">{expected}</strong>
        </p>
        <TextInput
          label={t('accounts.consolidate.confirmLabel')}
          required
          dir="ltr"
          autoComplete="off"
          spellCheck={false}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target?.value ?? e)}
          aria-describedby="accounts-consolidate-expected"
          // Not while they are still typing it: only once it is as long as the text.
          error={confirmation && confirmation.trim().length >= expected.length && !confirmed
            ? t('accounts.consolidate.confirmMismatch') : undefined}
          disabled={pending}
          data-testid="accounts-consolidate-confirm"
        />
      </div>
    </TaskDialog>
  );
}
